import { AppContext } from "App";

import { ISectionLogger, ISectionLoggerFactory, LoggingSection } from "@multiweb/logging";
import { IResultMessage } from "@multiweb/types/contracts";
import { IDisposable } from "Global/Base/IDisposable";
import { RightLevel, RightType } from "Global/Base/Rights/RightTypes";
import { limitCallPerInterval } from "Global/Base/Utils/FunctionUtils";
import { SetUtils } from "Global/Base/Utils/SetUtils";
import { ErrorHandler } from "UI/Errors";
import { SaveResultData } from "UI/UI.Controls/DataSource/EditableDBDataSource";
import { FormRightsExtractor, IFormRight } from "UI/UI/Rights/FormRight";
import { DeviceInfo } from "../../../../Device/Info/DeviceInfo";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { AsyncTree } from "../../../../Global/Base/DataTypes/AsyncTree";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { Event, IListener } from "../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { ROLELESS_ROLE_ID } from "../../../../Global/Base/Rights/constants";
import { ArrayUtils } from "../../../../Global/Base/Utils/ArrayUtils";
import { ConditionStore } from "../../../../OW.Remoting/Remoting/ConditionStore";
import { IOWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObjectList";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { EMPTY_REQUEST_CONTEXT } from "../../../../OW.Remoting/Remoting/RequestContext";
import { IBussyAwaitable } from "../../../UI.BaseControls/ControlInterfaces";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { SplitPanel, SplitterOrientation } from "../../../UI.ElementarControls/SplitPanel";
import { parseRequiredRightElementAttribute } from "../../Rights/Utils";
import { helper } from "../AlignPanelWidthCounter";
import { IHTMLElementWithControl } from "../Controls/ControlInterfaces";
import { IFRMParams } from "../Controls/FormParameters";
import { Registrator } from "../Controls/Registrator";
import { FormManager, IFormManager, IOpenedForm } from "../FormManager/IFormManager";
import { FormSizeComparer } from "../FormSizeComparer";
import { FormUICustomProperty, IFormMenu, IFormUI } from "../FormUI/FormUI";
import { FormPurpose } from "../IFormUIFactory";
import { MessageButtonType, MessageWindow } from "../MessageWindow";
import { UIHistoryStateFrame, UIManager } from "../UIManager";
import { IWindowable, WindowState } from "../UIManagerInterfaces";
import { OpenModeType } from "./Actions/DetailFormAction";
import { IPrimary } from "./Actions/IPrimary";
import { XGAction } from "./Actions/XGAction";
import { BaseForm, BaseFormParams } from "./BaseForm";
import { ControlInfo } from "./ControlInfo";
import { FormResult } from "./Forms/FormResult";
import { IFormProfileInfo } from "./Forms/IFormProfileInfo";
import { IFormProfileManager } from "./Forms/IFormProfileManager";
import { FrameSelChanged, IHideButtons } from "./Frames";
import { IMasterableFrame, IMFNoChangeCheck, ISpecificallyLoadableFrame } from "./Frames/FrameInterfaces";
import { FrameSharedMethods } from "./Frames/FrameSharedMethods";
import { ICalendarFrame, IDetailFrame, IDetailMasterableFrame, ISaveable, ISeparateDetailFrame } from "./Frames/IDetailMasterableFrame";
import { IEditableFrame } from "./Frames/IEditableFrame";
import { ITreeFrame } from "./Frames/ITreeFrame";
import { ITreeViewFrame } from "./Frames/ITreeViewFrame";
import { ITwoListFrame } from "./Frames/ITwoListFrame";
import { ListFrame } from "./Frames/ListFrame";
import { IExtendedLifeCycle } from "./IExtendedLifeCycle";
import { FormClosingCancellationReason, FormLayout, FrameRefreshType, IFormSavingParameters } from "./IForm";
import { IFrame, IFrameWithRights } from "./IFrame";
import { SharedMethods } from "./SharedMethods";
import { Page, Pages } from "./TabPages";
import { WidgetPanel } from "./WidgetPanel";
import { WindowedDetailFrame } from "./WindowedDetailFrame";

const FORM_BEHAVIOUR_SELECTOR = "[data-behaviour='form']";
const FRAME_BEHAVIOUR_SELECTOR = "[data-behaviour='frame']";
const DETAILFRAME_SUBBEHAVIOUR_SELECTOR = "[data-behaviour-subtype='detail']";
const DETAILFRAME_BEHAVIOUR_SELECTOR = `${FRAME_BEHAVIOUR_SELECTOR}${DETAILFRAME_SUBBEHAVIOUR_SELECTOR}`;
const NON_DETAILFRAME_FRAME_BEHAVIOUR_SELECTOR = `${FRAME_BEHAVIOUR_SELECTOR}:not(${DETAILFRAME_SUBBEHAVIOUR_SELECTOR})`;

export type FormParams = BaseFormParams & {
    parametricData?: IFRMParams;
};

export class Form extends BaseForm {
    //#region Fields
    /** Přepočtové koeficienty pro jednotlivé platformy */
    public static osCoef: { [index: string]: number } = {
        WP: 1.4,
        Android: 1.4,
        iOS: 1.4,
        BlackBerry: 1.4,
    };

    public static windowResized: Event<any, any> = new Event<any, any>();
    public static formLayoutChanged: Event<Form, any> = new Event<Form, any>();
    public static formShown: Event<any, any> = new Event<any, any>();

    public readonly allowResponsiveLayout: boolean;

    public purposeType: MenuType | undefined;
    public selectedId: string;

    /** ID položky menu, kterou byl formulář otevřen. */
    public menuItemId: number | undefined;

    public autoSelectFirstRow: boolean;
    public autoSelectOnlyIfOneRow: boolean;
    public horizontalLayout = SharedMethods.horizontalLayout;
    public frameToRefreshAfterSetFormActive: IFrame = null;
    public frameRefreshType: FrameRefreshType = FrameRefreshType.none;

    public applyLovCondition: boolean;
    public executerFrame: IFrame;
    private _detailFormObject: OWObject;
    public get detailFormObject(): OWObject {
        return this._detailFormObject;
    }
    public formCondition?: string;
    public formConditionParamAttrs?: readonly string[];
    public formConditionParamsObject?: OWObject;
    public masterLinkToAttr: string;

    private _titleLocKey: string;
    public get titleLocKey(): string {
        return this._titleLocKey;
    }
    public set titleLocKey(value: string) {
        this._titleLocKey = value;
    }
    public inModalWindow: boolean;

    public isLov: boolean;
    public enableLovSelect: boolean;
    // Jestli objekt IWindowable musí být zobrazený na stejném okně, než už je otevřeno
    // Nastavuje se na True jen v případě, že formulář na modálním okně se otevře pomocí doubleClick na podřízeném seznamu
    // nebo pokud otevírá nový formulář po stlačení šipky na modálním okně s IMPLICIT DetailFrame
    public showInSameWindow: boolean;
    public disableFormMenu: boolean = false;
    public backButton: XGAction;
    public detailFormMasterFrame: IMasterableFrame;
    public isModal: boolean;

    protected _menu: IFormMenu;
    protected _formInternalPanel: Element;

    private readonly _logger: ISectionLogger;
    private readonly _splitPanelDefaultSplitterPositions: Map<string, number> = new Map<string, number>();
    /** Titulek formu. Má prioritu před titleLocKey */
    private _customTitle: string;
    /**
     * Indikuje, zda již byly poprvé nastaveny parametry metody setParametricData.
     * Použito, aby za běhu nešly měnit všechny přijímané parametry - mohlo by rozbít funkčnost.
     */
    private _parametricDataFirstSet = false;
    private _outerPanel: HTMLDivElement = document.createElement("div");
    private _splitterMoveListener: EventListener;
    private _keyupControlListener: IListener;
    private _clickListener: EventListener;

    private _insertObjectName?: string;
    private _hideButtons?: IHideButtons;
    private _masterObject: OWObject;
    private _masterObjectList: IOWObjectList;
    /**
     * Výchozí pozice splitterů ve SplitPanelech ma formuláři (potřeba pro zrušení aktivího profilu)
     */
    private _formDesign: FormDesign;
    // Přepočtový koeficient pro touch zařízení
    private _resizeCoeficient: number = 1;

    private _mainFrame: IFrame;
    // Listener volaný při otevření formuláře
    private _formOpenedListener: IListener;
    // Listener volaný při přepnutí mezi formuláři
    private _formSwitchedListener: IListener;
    // Listener volaný při přepnutí na horizontální rozložení formuláře
    private _horizontalResizeListener: IListener;
    // Listener volaný při přepnutí na vertikální rozložení formuláře
    private _verticalResizeListener: IListener;
    // Permanentní listener, který je volaný při každé změně velikosti formuláře
    private _permanentResizeListener: IListener;
    private _treeHierarchy: AsyncTree<IFrame>;
    private _resultMessages: readonly IResultMessage[];
    private _closeDisabled: boolean = false;
    private _isFavorite: boolean = false;
    private _disableQueryMode: boolean = false;

    private _initialFormProfileId: number;

    private _childForms: Array<IWindowable> = new Array<IWindowable>();
    private _widgetIntersectionObserver: IntersectionObserver | undefined = undefined;
    private _formLayout: FormLayout = FormLayout.none;

    private _rightsSettingsVersion: string;
    //#endregion Fields

    //#region Properties
    public get version(): string | undefined {
        return this.element.getAttribute("data-formversion");
    }

    public get masterObject(): OWObject {
        return this._masterObject;
    }

    public set masterObject(val: OWObject) {
        this._masterObject = val;
        this.masterObjectChanged.trigger(this, val);
    }

    public get masterObjectList(): IOWObjectList {
        return this._masterObjectList;
    }
    public set masterObjectList(val: IOWObjectList) {
        this._masterObjectList = val;
    }

    /** Titulek formu. Má prioritu před titleLocKey */
    public get customTitle(): string {
        return this._customTitle;
    }

    /** Titulek formu. Má prioritu před titleLocKey */
    public set customTitle(value: string) {
        if (value === this._customTitle) {
            return;
        }

        this._customTitle = value;

        this.titleChanged.trigger(this, value);
    }

    /** Název třídy zakládaného záznamu při OpenMode.insert */
    public get insertObjectName(): string | undefined {
        return this._insertObjectName;
    }

    /**
     * Tlačítka, která mají být navíc skrytá na hlavním framu
     */
    public get hideButtons(): IHideButtons | undefined {
        return this._hideButtons;
    }

    public get formDesign(): FormDesign {
        return this._formDesign;
    }

    public set formDesign(formDesign: FormDesign) {
        if (!formDesign)
            formDesign = FormDesign.desktop;
        switch (this._formDesign) {
            case FormDesign.phone:
            //this.element.classList.remove("FormDesignPhone"); removePhoneStyles
            // falls through
            case FormDesign.tablet:
            //this.element.classList.remove("FormDesignTablet"); removePhoneStyles
            // falls through
            case FormDesign.desktop:
                this.element.classList.remove("FormDesignDesktop");
        }
        this._formDesign = formDesign;
        switch (this._formDesign) {
            case FormDesign.phone:
            //this.element.classList.add("FormDesignPhone"); removePhoneStyles
            // falls through
            case FormDesign.tablet:
            //this.element.classList.add("FormDesignTablet"); removePhoneStyles
            // falls through
            case FormDesign.desktop:
                this.element.classList.add("FormDesignDesktop");
        }
    }

    public override get isEdited(): boolean {
        const ef = this._checkMasterableFramesIfEdited({ cancelEditIfNotEdited: false });
        return ef.some(frm => frm instanceof IEditableFrame && frm.isEditing && frm.isChanged);
    }

    public addPendingAction(): void {
        throw new Error("Method not implemented.");
    }
    public waitForPendingActions(): Promise<void> {
        return Promise.resolve();
    }

    /**
     * Frame, který byl při inicializaci označen jako hlavní
     */
    public get mainFrame(): IFrame {
        return this._mainFrame;
    }

    /** Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data */
    public get queryMode(): boolean | null {
        if (this._disableQueryMode && !this._queryMode.formValue) // Pokud je query mode pro formulář natvrdo na true, tak ho lze ovlivnit pouze profilem, ale ne způsobem otevření.
            return false;

        if (this._queryMode.profileValue != null) {
            return this._queryMode.profileValue;
        }

        if (this._queryMode.formValue != null) {
            return this._queryMode.formValue;
        }

        return null;
    }

    public override get profileManager(): IFormProfileManager {
        return this._profileManager;
    }

    /**
     * Nastavení profilu pro formulář
     */
    public override get profileInfo(): IFormProfileInfo {
        const data: IFormProfileInfo = { splitPanels: {} };

        for (const sp of this.element.querySelectorAll(SplitPanel.is) as NodeListOf<SplitPanel>) {
            data.splitPanels[sp.id] = { position: Math.round(sp.currentSplitterPosition * 100) / 100 }; // Zaokrouhlení na 2 desetinná místa
        }

        return data;
    }

    public override set profileInfo(profileInfo: IFormProfileInfo) {
        if (!profileInfo) {
            this._queryMode.profileValue = null;

            return;
        }

        this._queryMode.profileValue = profileInfo.queryMode;

        if (profileInfo.splitPanels) {
            for (const [id, spProfile] of Object.entries(profileInfo.splitPanels)) {
                const sp = document.getElementById(id) as SplitPanel;
                if (!sp)
                    continue;

                sp.currentSplitterPosition = spProfile.position;
            }
        }
    }

    /**
     * Práva definována na úrovni formuláře
     */
    public override get formRights(): Array<IFormRight> {
        return this._formRights;
    }

    public override get openInEdit(): boolean {
        return this._openInEdit;
    }

    public override set openInEdit(v: boolean) {
        this._openInEdit = v;
    }

    /**
     * Vrací id profilu, který se má po otevření formuláře aplikovat
     */
    public override get initialFormProfileId(): number {
        return this._initialFormProfileId;
    }
    //#endregion Properties

    //#region Constructors
    /**
     * Pro inicializaci je potřeba navíc volat metodu init
     * @param ownerForm
     * @param element
     * @param formInstanceId Identifikátor otevřené instance formuláře.
     * @param parametricData
     * @param registerToBrowserHistory
     */
    constructor({ parametricData, registerToBrowserHistory = true, ...otherParams }: FormParams) {
        super({ registerToBrowserHistory, ...otherParams });
        this._logger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.form);

        this._checkUniqueIds();
        this._rightsSettingsVersion = this.element.getAttribute("data-rightssettingsversion");

        if (this.ownerForm && parametricData.openInEdit)
            this.ownerForm.readOnly = true;

        if (parametricData) {
            this.setParametricData(parametricData);
        } else
            this._parametricDataFirstSet = true;

        this._formInternalPanel = this.element.querySelector("div[data-tsccontrol='FormInternalPanel']");
        this.titleLocKey = this.element.getAttribute("data-captionlockey");
        this.allowResponsiveLayout = this.getBoolDataAttr("AllowResponsiveLayout", true);

        if (this.element.hasAttribute("data-queryMode")) {
            this._queryMode.formValue = this.getBoolDataAttr("QueryMode");
        }
    }
    //#endregion Constructors

    //#region Methods
    /**
     * Inicializace formuláře
     */
    public override async init(formUI: IFormUI): Promise<void> {
        try {
            this.canClose = true;

            this.element.classList.add("Form");
            if (!this.element.id)
                this.element.id = UIManager.instance.generateElementID();

            this._adjustForDevice();
            this._setResizeEvent();

            // Vytvoří práva na úrovni formuláře
            await this._initializeFormRights();

            this._tagWidgetElements();

            // Inicializuje tsc controly u nalezených elementů
            await Registrator.initializeFormControlsWithFilter(this, el => !this._isElementInWidget(el), this.element);

            this._initializePrimary();

            // Naplní this._masterableFrames
            await this._initializeFrames(el => {
                // na detailovém formuláři přeskočí všechny framy, které nejsou DetailFrame nebo na něm, protože se později stejně zahodí
                if (this._isDetailForm && !this._isDetailFrameOrInDetailFrame(el))
                    return false;
                // a všechny elementy co jsou ve WidgetPanelech
                if (this._isElementInWidget(el))
                    return false;
                return true;
            });

            if (this._isDetailForm && this.detailFrame == null)
                throw new Error("Opening detail form without DetailFrame");

            this._logger.info(`Form ${this.formName} is displayed as a detail form. All other frames were suppressed.`);

            await this._profileManager.preloadSavedProfiles();
            this._initializeListeners();
            await this._profileManager.initializeProfiles(this._initialFormProfileId); // Po načtení profilů prochází this._masterableFrames a nastavuje je

            if (this._isDetailForm) {
                this._inializeDetailFormMasterFrame();
                this._removeNonDetailFrames();
            }

            this._createTreeHiearchy();

            if (this._mainFrame instanceof IMasterableFrame && this._mainFrame.permanentInvisible && this._detailFrame)
                this.visibleOnlyDetail = true;

            this.formUI = formUI ?? DIContainer.instance.create(IFormUI, this, this.isLov ? FormPurpose.lov : FormPurpose.standard);
            this._menu = this.formUI.createFormMenu();

            // Inicializace všech framů - postupuje se od framů bez mastera k jejich podřízeným
            // Je zajištěno, že když se inicializuje podřízený frame, tak už jsou všechny jeho nadřízené framy zinicializované
            await this._treeHierarchy.traverseRootsFinishFirstAsync(f => f.initialize());
            this.framesInitialized.trigger(this, null);

            this._initSplitterPositions();

            if (this.formCondition && this._masterableFrames.filter(f => f.isMainFrame).length > 1)
                this._logger.error(`Form is opened with Condition and there is more than one main frame - all such frames will use this condition '${this.formCondition}'!`); //`

            this._initFirstframe();

            if (this.ownerForm) {
                this.ownerForm.childOpened.trigger(this, undefined);
            }

            this._setFormLayout();
            this._initFilterPanel();

            this.formProfileChanged.add(() => {
                this.profileManager.setActualProfileEdited();
            });

            if (this._resultMessages)
                UIManager.instance.showResultMessages(this._resultMessages);

            this._watchWidgetDisplaying();
        } catch (er) {
            ErrorHandler.instance.handleError(er);
        }
    }

    /**
     * Inicializace listenerů.
     */
    private _initializeListeners(): void {
        this._initChildrenOpenCloseListeners();

        this._events.addListener(this.element, "click", this._clickListener = (e: MouseEvent) => {
            this._clickMethod(e);
        });

        this._events.add(this.profileManager.selectedProfileChanged, (profileItem) => {
            if (profileItem.basic)
                this.customTitle = undefined;
            else
                this.customTitle = profileItem.title;
        });
    }

    private _isDetailFrameOrInDetailFrame(frame: HTMLElement): boolean {
        return frame.matches(DETAILFRAME_BEHAVIOUR_SELECTOR) || GenericControl.hasParent(frame, parent => parent.matches(DETAILFRAME_BEHAVIOUR_SELECTOR));
    }

    /**
     * Odstraní framy, keré nejsou DetailFramem ani na něm neleží (typicky použité v detailových formulářích)
     */
    private _removeNonDetailFrames(): void {
        const frames = Array.from(this.element.querySelectorAll(NON_DETAILFRAME_FRAME_BEHAVIOUR_SELECTOR)) as HTMLElement[];

        for (const frame of frames) {
            if (!GenericControl.hasParent(frame, p => p.matches(DETAILFRAME_BEHAVIOUR_SELECTOR)))
                frame.remove();
        }
    }

    private _watchWidgetDisplaying(): void {
        const widgets = Array.from(this.element.querySelectorAll(FORM_BEHAVIOUR_SELECTOR)) as IHTMLElementWithControl[];
        if (widgets && widgets.length > 0) {
            const options: IntersectionObserverInit = {
                root: this.element,
                rootMargin: "0px",
                threshold: 0, // nastavuje se misto na sledovanem elementu, pri kterem se aktivuje protnuti a zacne se s inicializaci. Zdava se v procentech zadanym desetinnym cislem. Tedy 0.5 je 50%
            };
            // Provede se zalozeni IntersectionObserveru, ktery bude hlidat, zda se element/widget dostane do viewportu uzivatele a na zaklade nastaveni v option vyhodnoti, zda se spusti nacitani nebo ne
            this._widgetIntersectionObserver = new IntersectionObserver((entries) => {
                let checkPages = false;
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        ((entry.target as IHTMLElementWithControl).tscControl as WidgetPanel).init();
                        this._widgetIntersectionObserver.unobserve(entry.target);
                        checkPages = true; // Pokud se inicializoval nějaký widget, tak je potřeba znovu zkontrolovat pages.
                    }
                }
                if (checkPages)
                    Form._checkPages();
            }, options);
            for (const widget of widgets) {
                if (widget.tscControl instanceof WidgetPanel) {
                    this._widgetIntersectionObserver.observe(widget);
                }
            }
        }
    }

    /**
     *
     * @param predicate Predikát určuje, zda se má control elementu inicializovat
     */
    private async _initializeFrames(predicate: (element: HTMLElement) => boolean): Promise<void> {
        const elements = Array.from(this.element.querySelectorAll(FRAME_BEHAVIOUR_SELECTOR)) as IHTMLElementWithControl[];

        if (elements.length > 0) {
            for (const el of elements) {
                if (!predicate(el))
                    continue;

                if (!el.tscControl) {
                    await Registrator.initializeFormControls(this, el);
                    this._logger.info(`Initializing IFrame ${el.getAttribute("data-tsccontrol")} ${el.getAttribute("data-framename")} control in second pass. This may signal an error in form initialization process!`);
                }

                if (!el.tscControl) {
                    this._logger.error(`Unable to initialize IFrame ${el.getAttribute("data-tsccontrol")} ${el.getAttribute("data-framename")} control in second pass. Skipped!`);
                    continue;
                }

                if (!(el.tscControl instanceof IFrame))
                    throw new InvalidOperationException(`Initializing component '${el.getAttribute("data-tsccontrol")}' '${el.getAttribute("data-framename")}' of data-behaviour 'frame' but it doesn't implement interface IFrame!`);

                const frame = el.tscControl;
                if (frame instanceof IDetailFrame) {
                    if (!this.detailFrame) {
                        this._detailFrame = frame;
                    }

                    if (this.visibleOnlyDetail) {
                        this._detailFrame.element.classList.add("onlyDetailShow");
                    }
                } else {
                    // Pokud má zobrazovat pouze detail všechny ostatní prvky se skryjí. V případě, že jedná o vnořený frame uvnitř jiného IDetailFrame, tak visibleOnlyDetail na vnořených framech má být ignorována (bug #19985)
                    if (this.visibleOnlyDetail && GenericControl.allParents(frame.element, (el: IHTMLElementWithControl) => !(el.tscControl instanceof IDetailFrame))) {
                        frame.visible = false;
                        if (frame instanceof IMasterableFrame) {
                            frame.permanentInvisible = true;
                        }
                        this.element.classList.add("onlyDetailShow");
                    }
                }

                if (!frame.frameName)
                    this._logger.warn(`[${el.getAttribute("data-tsccontrol")}] Frame has empty FrameName property`);

                let isTopLevelFrame = false;
                // Kontrola, zda je frame na hlavní úrovni formuláře (v podstatě že není vnořený v žádném jiném framu, ale zároveň může být v nějakém panelu)
                if (!GenericControl.hasParent(el.parentElement, p => p.matches(FRAME_BEHAVIOUR_SELECTOR))) {
                    isTopLevelFrame = true;
                    this._topLevelFrames.push(frame);
                }

                if (frame instanceof IMasterableFrame) {
                    this._masterableFrames.push(frame);
                    if (isTopLevelFrame)
                        frame.selectionChanged.add(this._showNextFrameEventMethod);
                }

                this._frames.push(frame);
            }
        }
    }

    private _tagWidgetElements(): void {
        const widgets = Array.from(this.element.querySelectorAll(FORM_BEHAVIOUR_SELECTOR)) as IHTMLElementWithControl[];
        for (const widget of widgets) {
            const widgetElements = widget.querySelectorAll("[data-tsccontrol]");
            for (const widgetElemnt of widgetElements) {
                widgetElemnt.setAttribute("data-in-widget", "true");
            }
        }
    }

    private _isElementInWidget(el: HTMLElement): boolean {
        return GenericControl.hasParent(el, parent => parent.matches(FORM_BEHAVIOUR_SELECTOR) && parent != this.element);
    }

    /**
     * Metoda spouštěná pokud se zaktivuje formulář při práci s modálními okny
     */
    public override async actualizeForm(): Promise<void> {
        for (const masterableFrame of [...this._masterableFrames, this.detailFormMasterFrame]) {
            if (masterableFrame && masterableFrame instanceof IFrame) {
                if (masterableFrame.actualObject?.isChanged || masterableFrame.actualObject?.isNew) {
                    continue;
                }

                if (masterableFrame.refreshListAfterSetActive) {
                    await masterableFrame.refreshData(true);

                    masterableFrame.refreshListAfterSetActive = false;
                } else if (masterableFrame.refreshObjectAfterSetActive) {
                    await masterableFrame.refreshActualObject();

                    masterableFrame.refreshObjectAfterSetActive = false;
                }
            }
        }
    }

    /** Metoda pro listener kliku */
    @limitCallPerInterval(1000, true)
    private _clickMethod(e: MouseEvent): void {
        if (e.ctrlKey || e.metaKey)
            ControlInfo.displayInfo(e.target as HTMLElement, null);
    }

    /**
     * Po zavření formuláře s detailFormMasterFrame (modálně otevřený detail) se zruší provázání na nový DetailFrame
     */
    private _inializeDetailFormMasterFrame(): void {
        if (!this.detailFormMasterFrame)
            return;

        const detailFormMasterFrame: IMasterableFrame = this.detailFormMasterFrame; // closure

        this.closed.add(() => {
            if (detailFormMasterFrame instanceof IDetailMasterableFrame) {
                if (detailFormMasterFrame.detail)
                    detailFormMasterFrame.detail.masterFrame = undefined; // Je potřeba odstranit modálnímu detailu referenci na masterFrame, jinak by později ve své funkci dispose() odstranil referenci z masterFrame na původní (nemodální) detail
                detailFormMasterFrame.detail = undefined;
            }

            if (detailFormMasterFrame.ownerForm) {
                for (const frame of detailFormMasterFrame.ownerForm.frames) { // Pokusí se zpět navázat původní detail, pokud existuje
                    if (detailFormMasterFrame instanceof IDetailMasterableFrame && frame instanceof IDetailFrame && detailFormMasterFrame.frameName === frame.masterFrameName) {
                        detailFormMasterFrame.detail = frame;
                        detailFormMasterFrame.detail.masterFrame = detailFormMasterFrame;
                        break;
                    }
                }
            }
        });
    }

    /**
     * Vytvoření stromové struktury framů a jejich propojení master-slave a master-detail
     */
    private _createTreeHiearchy(): void {
        this._treeHierarchy = new AsyncTree<IFrame>();
        for (const f of this._frames) {
            const master = this._getMasterFrame(f);
            if (master) {
                // Propojení framů
                f.masterFrame = master;
                if (master instanceof IDetailMasterableFrame && f instanceof IDetailFrame)
                    master.detail = f;

                // V případě, že leží frame na detailu, použije se jako parent node detail místo jeho master framu, aby se pak provedla inicializace framů ve správném pořadí.
                const parentDetail = ((GenericControl.findParent(f.element,
                    el => el instanceof IHTMLElementWithControl && el.tscControl instanceof IDetailFrame) as IHTMLElementWithControl)
                    ?.tscControl as IDetailFrame);

                this._treeHierarchy.addToNode(f, parentDetail && parentDetail.masterFrameName === master.frameName ? parentDetail : master);
            } else {
                this._treeHierarchy.add(f);
            }
        }
        this._detectMainFrame();
    }

    private static _detectIsFrameMainFrame(frame: IFrame): boolean {
        // Na SeparateDetailFrame není žádný frame hlavním - nemá to tam význam
        if (frame instanceof ISeparateDetailFrame)
            return false;

        // Není hlavním, když má frame nějaký nadřízený frame
        if (frame.masterFrame)
            return false;

        const detailFrame = frame.ownerForm && frame.ownerForm.detailFrame;
        if (!detailFrame) {
            // Na formuláři bez detailu a bez uvedení masterFrame je takový frame brán jako hlavní
            // teoreticky by jich mohlo být víc, ale pak to nejde rozhodnout a takový případ není podporovaný
            return true;
        }

        // Poslední možnost je, že detail má delší řetěz masterFramů (např. DetailFrame -> ListFrame -> ListFrame),
        // tak se rekurzivně projde přes všechny mastery od detailu a porovnává se, zda je hledaný na konci tohoto řetězu
        let masterFrame = detailFrame.masterFrame;

        do {
            // Frame nemá mastera, není kde dál hledat
            if (!masterFrame)
                return false;

            // Pokud je hledaný frame na konci řetězu, je brán jako hlavní
            if (masterFrame === frame)
                return true;

            // Vezme se další frame v řetězu
            masterFrame = masterFrame.masterFrame;
        } while (masterFrame); // Pokračuje se dokud je k dispozici další frame v řetězu

        return false;
    }

    /**
     * Vrací masterFrame daného IFramu
     */
    private _getMasterFrame(sender: IFrame): IMasterableFrame {
        if (!sender.masterFrameName)
            return null;

        if (this._isDetailForm && sender == this.detailFrame)
            return this.detailFormMasterFrame;

        let masterFrame: IMasterableFrame = null;

        for (const mf of this.masterableFrames) {
            if (mf.frameName == sender.masterFrameName) {
                masterFrame = mf;
                break;
            }
        }

        if (!masterFrame && this._isDetailForm) {
            const wdf = this.masterableFrames.find(f => f instanceof WindowedDetailFrame);
            if (wdf) // WindowedDetailFrame převezme roli masterFrame
                masterFrame = wdf;
            else // framy, které jsou v původní definici napojeny na zrušený frame, se napojí na předaný master pro detail
                masterFrame = this.detailFormMasterFrame;
        }

        // TODO: Tohle by si asi měl řešit sám frame, je to tu jen, aby ta funkce fungovala jako původně než se zjisti kde se to používá všude
        if (masterFrame && sender.masterFrame && sender instanceof IMasterableFrame) {
            sender.masterObject = sender.masterFrame.actualObject;
        }

        return masterFrame;
    }

    private _showNextFrameEventMethod = (sender: IFrame, data: FrameSelChanged) => {
        const showNext = sender.element.classList.contains("VisibleFrame");
        if (data.actualObject && showNext)
            this._showNextFrame(sender);
    };

    private _showNextFrame(currentFrame: IFrame): void {
        for (const nextFrame of this._topLevelFrames) {
            if (nextFrame.masterFrame == currentFrame) {
                this.setVisibleFrame(nextFrame);
                break;
            }
        }
    }

    /**
     * Zkontroluje všechny masterableFramy na formuláři, zda jsou rozeditované. Jestli jo, tak je vrací.
     * @param savingParams Parametry ukládání na formuláři.
     */
    private _checkMasterableFramesIfEdited(savingParams?: IFormSavingParameters): IMasterableFrame[] {
        const editedFrames: IMasterableFrame[] = [];
        const frames: Set<IMasterableFrame> = this._getFrames(null, savingParams.openedModalFrames, savingParams.mostTopFrame);

        for (const currentFrame of frames) {
            if (!currentFrame || currentFrame === savingParams.sourceFrame)
                continue;
            if (currentFrame instanceof ITreeFrame && currentFrame.isSubframe(savingParams.sourceFrame))
                continue;
            if (currentFrame instanceof ITwoListFrame && currentFrame.isSubframe(savingParams.sourceFrame))
                continue;
            if (currentFrame instanceof IMFNoChangeCheck && ((!(currentFrame instanceof IMasterableFrame)) || currentFrame !== this.detailFormMasterFrame))
                continue;

            if (currentFrame.actualObject && (currentFrame.actualObject.isChanged || currentFrame.actualObject.isNew) && !currentFrame.isEditContextInitiator) {
                editedFrames.push(currentFrame);
                continue;
            }

            if (currentFrame?.isEditContextInitiator) {
                editedFrames.push(currentFrame);
                continue;
            }

            if (currentFrame instanceof IEditableFrame && currentFrame.isEditing) {
                if (currentFrame.isChanged) {
                    editedFrames.push(currentFrame);
                    continue;
                } else {
                    if (savingParams.cancelEditIfNotEdited && !savingParams.formClosing && !currentFrame.isEditContextInitiator) {
                        currentFrame.isEditing = false;
                    }
                }
            }
        }
        return editedFrames;
    }

    /**
     * Počká na probíhající procesy na předaném framu a jeho podřízených framech
     * @param sourceFrame Frame, na kterém má začít čekání
     */
    public override async waitForBussyFrameAndChildren(sourceFrame: IFrame): Promise<void> {
        const subTree = this._treeHierarchy.findByElement(sourceFrame);

        if (subTree == null) return;
        await subTree.traverseRootsFinishFirstAsync(async (frame) => {
            if (frame instanceof IBussyAwaitable)
                await frame.waitForPendingActions();
        });
    }

    public override setState(state: WindowState, value: any): void {
        switch (state) {
            case WindowState.active:
                if (this._isActive != value) {
                    this._isActive = value;
                    //this._menu.setProperty(FormUICustomProperty.formMenuVisibility, value);
                } else {
                    return;
                }
                break;
            case WindowState.closeDisabled:
                if (this._closeDisabled != value) {
                    this._closeDisabled = value;
                } else {
                    return;
                }
                break;
            case WindowState.favorite:
                if (this._isFavorite != value) {
                    this._isFavorite = value;
                } else {
                    return;
                }
                break;
            case WindowState.realVisibility: {
                this._realVisibility = value;
                this._menu?.setProperty(FormUICustomProperty.formMenuVisibility, value);
                break;
            }
            case WindowState.visible: {
                if (this._formVisible != value) {
                    this._formVisible = value;
                    this._menu?.setProperty(FormUICustomProperty.formMenuVisibility, value);
                    if (value) {
                        BaseForm.resizeEventMethod();
                    }
                }
                break;
            }
        }
        this.stateChanged.trigger(this, { state, value });
    }

    public override getState(state: WindowState): boolean {
        switch (state) {
            case WindowState.active:
                return this._isActive;
            case WindowState.closeDisabled:
                return this._closeDisabled;
            case WindowState.favorite:
                return this._isFavorite;
        }

        return false;
    }

    private async unlockObjectsOnFrames(): Promise<void> {
        const frames: Set<IMasterableFrame> = this._getFrames(true);

        for (const currentFrame of frames) {
            if (currentFrame instanceof IExtendedLifeCycle && currentFrame instanceof IEditableFrame && currentFrame.isEditing)
                await currentFrame.unlockObject();
        }
        if (this.detailFrame && this.detailFrame instanceof IExtendedLifeCycle)
            await this.detailFrame.unlockObject();
    }

    /**
     * Zavírá formulář.
     */
    public override async close(result?: FormResult): Promise<boolean> {
        await Promise.all(this._childForms.map((form) => form.close()));

        // vymaže resize listenery
        this._clearResizeListener(this._horizontalResizeListener);
        this._clearResizeListener(this._verticalResizeListener);
        this._clearResizeListener(this._permanentResizeListener);

        if (this._childForms.length > 0) {
            return false;
        }

        const res = await this.checkAndSaveForm({ cancelEditIfNotEdited: true, formClosing: true });
        await this.unlockObjectsOnFrames();
        if (res) {
            await this.setToolbarToEditMode(false);
            return await this.closingPreview.trigger(this, null).then(async (cancelEvent) => {
                if (!cancelEvent) {
                    if (this.ownerForm) {
                        await Promise.all(this.ownerForm.masterableFrames.map(async frame => frame.rightsEvaluationTask));
                        this.ownerForm.childClosed.trigger(this, undefined);
                        this.ownerForm.setState(WindowState.active, true);
                    }
                    await Promise.all(this.masterableFrames.map(async frame => frame.rightsEvaluationTask));
                    this.closed.trigger(this, result);
                    this.dispose();
                    return true;
                }
                this.closingCancelled.trigger(this, {
                    cancellationReason: FormClosingCancellationReason.confirmOnCloseAborted,
                });
                return false;
            });
        }
        this.closingCancelled.trigger(this, {
            cancellationReason: FormClosingCancellationReason.yncAborted,
        });
        return false;
    }

    public setVisibleFrame(frame: IFrame, addToUIHistory?: boolean): void;
    public setVisibleFrame(frame: string, addToUIHistory?: boolean): void;
    public override setVisibleFrame(frame: any, addToUIHistory: boolean = true): void {
        for (const topFrame of this._topLevelFrames)
            topFrame.element.classList.remove("VisibleFrame");

        let iFrame: IFrame;
        if (typeof frame === "string") {
            for (const topFrame of this._topLevelFrames)
                if (frame === topFrame.frameName) {
                    iFrame = topFrame;
                    break;
                }
        } else
            iFrame = frame;

        iFrame.element.classList.add("VisibleFrame");
        iFrame.frameIsVisible.trigger(iFrame, null);

        if (addToUIHistory) {
            UIManager.instance.addUIHistory({ type: "Frame", formID: this.element.id, formName: this.formName, frameName: iFrame.frameName } as UIHistoryStateFrame, iFrame.frameName);
        }
    }

    /** Zjišťuje, zda jsou framy směrově propojené v řetězci master framů.
     * @param topFrame Nadřízený frame.
     * @param lowFrame Podřízený frame.
     */
    private _areFramesDirectionallyConnected(topFrame: IFrame, lowFrame: IFrame): boolean {
        if (!topFrame || !lowFrame)
            return true;

        let currentFrame = lowFrame;
        while (currentFrame) {
            if (currentFrame === topFrame)
                return true;
            currentFrame = currentFrame.masterFrame;
        }
        return false;
    }

    private _getFrames(withoutReplacingMasterFrame = false, sourceFrameOpenedModalFrames?: readonly IMasterableFrame[], mostTopFrame?: IMasterableFrame): Set<IMasterableFrame> {
        let frames: Set<IMasterableFrame> = new Set();

        if (this._masterableFrames.length > 0) {
            for (const frame of this._masterableFrames) {
                if (this._areFramesDirectionallyConnected(mostTopFrame, frame)) {
                    frames.add(frame);
                }
            }
        } else {
            for (const topFrame of this._topLevelFrames) {
                if (this._areFramesDirectionallyConnected(mostTopFrame, topFrame))
                    frames.add(topFrame.masterFrame);
            }
        }

        if (!withoutReplacingMasterFrame) {
            if (this.isModal && this.detailFormMasterFrame && this._areFramesDirectionallyConnected(mostTopFrame, this.detailFormMasterFrame)) {
                frames.add(this.detailFormMasterFrame);
            }
        } else {
            frames.delete(this.detailFormMasterFrame);
        }

        if (sourceFrameOpenedModalFrames) {
            const modalFrames = new Set(sourceFrameOpenedModalFrames);
            frames = SetUtils.union(frames, modalFrames);
        }

        return frames;
    }

    public override async setToolbarToEditMode(inEdit: boolean): Promise<void> {
        if (this._detailFrame)
            await this._detailFrame.setDetailToEditMode(inEdit, true);
    }

    private _initializePrimary(): void {
        const elements = Array.from(this.element.querySelectorAll("[data-isprimary]")) as IHTMLElementWithControl[];
        let primaryFound: boolean = false;
        for (const el of elements) {
            if (el.tscControl instanceof IPrimary) {
                const control = el.tscControl as IPrimary;
                if (control.isPrimary) {
                    if (primaryFound) break;
                    else primaryFound = true;
                    window.addEventListener("keyup", this._keyupControlListener = (e: KeyboardEvent) => {
                        if (e.keyCode == 13 && control.visible && control.isActive) {
                            if (elements.length > 1) this._logger.warn("2 or more items with parameter IsPrimary=\"true\"!");
                            control.execute();
                        }
                    });
                }
            }
        }
    }

    /**
   * detekce hlavního framu - je potřeba mít vytvořeno provázání framů master-slave
   */
    private _detectMainFrame(): void {
        for (const f of this._frames) {
            const isFrameMain = Form._detectIsFrameMainFrame(f);
            if (isFrameMain) {
                f.isMainFrame = !this.mainFrame; // Pokud byl frame detekován jako hlavní a ještě nebyl označen jako hlavní žádný jiný, nastaví se mu, že je hlavní. Pokud už jiný byl označen jako hlavní, aktuální se nastaví jako ne-hlavní.
                if (!this.mainFrame) { // Pokud ještě nebyl žádný frame označen jako hlavní
                    this._mainFrame = f; // Zapamatuji si na Formu jeho hlavní frame
                    if (this._mainFrame instanceof IFrameWithRights)
                        this._mainFrame.formCanRead = this._canRead;
                    this._logger.info(`Frame of type '${f.element.getAttribute("data-tsccontrol")}' with name '${f.frameName}' marked as main`); //`

                    if (this.openMode === OpenModeType.edit) {
                        this.openInEdit = true;
                    }
                } else {
                    this._logger.warn(`Frame of type '${f.element.getAttribute("data-tsccontrol")}' with name '${f.frameName}' was detected as main, even though frame '${this.mainFrame.element.getAttribute("data-tsccontrol")}' with name '${this.mainFrame.frameName}' has already been marked as main before. Only second mentioned will be kept marked as main.`); // `
                }
            } else {
                f.isMainFrame = false;
            }
        }
    }

    /**
     * Upraví zobrazení formuláře podle zařízení
     */
    private _adjustForDevice(): void {
        if (DeviceInfo.deviceProperties.touch)
            this._enlargeForTouch(this.element); // zvetseni Detailu pro touch zarizeni

        if (DeviceInfo.deviceProperties.phone)
            this.formDesign = FormDesign.phone;
        else {
            this.formDesign = FormDesign.desktop; // pridat detekci podle rozmeru
        }
    }

    private _setResizeEvent(): void {
        Form.formLayoutChanged.removeAll();

        FormSizeComparer.setResizeListeners(this.element, false, UIManager.instance.windowResized, this.horizontalLayout);
        this._permanentResizeListener = FormSizeComparer.listeners[FormSizeComparer.listeners.length - 1]; // přiřadí právě vytvořený resize listener z předchozího řádku

        if (!DeviceInfo.deviceProperties.phone) {
            FormManager.instance.formOpened.add(this._formOpenedListener = () => {
                Form.checkFormSizeAfterShown(this);
            });
            FormManager.instance.formSwitched.add(this._formSwitchedListener = (_formManager: IFormManager, openedFormInfo: IOpenedForm) => {
                if (openedFormInfo.openedFormInfo.formInstanceId === this.formInstanceId) {
                    Form.checkFormSizeAfterShown(this);
                }
            });
        }
    }
    /**
     * Uložení výchozí pozice splitterů ve SplitPanelech
     */
    private _initSplitterPositions(): void {
        for (const sp of this.element.querySelectorAll(SplitPanel.is) as NodeListOf<SplitPanel>) {
            // bohužel polyfill pro CustomElements neumožňuje v tuto chvíli číst pomocí property (ještě neproběhl upgrade), tak je nutné číst přímo atribut :(
            const s = sp.getAttribute("splitter-position");
            this._splitPanelDefaultSplitterPositions.set(sp.id, s && !isNaN(+s) ? +s : 50);
        }
        this.element.addEventListener("splittermove", this._splitterMoveListener = () => this.formProfileChanged.trigger(this, undefined));
    }

    private _initFirstframe(): void {
        const firstFrame = this._topLevelFrames[0];
        if (!firstFrame) {
            return;
        }
        firstFrame.element.classList.add("VisibleFrame");

        if (this._registerToBrowserHistory)
            UIManager.instance.addUIHistory({ type: "Form", formID: this.element.id, formName: this.formName, frameName: firstFrame.frameName } as UIHistoryStateFrame, firstFrame.frameName);
    }

    private _initChildrenOpenCloseListeners(): void {
        this._events.add(this.childClosed, (sender) => {
            this._childForms.splice(this._childForms.indexOf(sender), 1);
            if (this._childForms.length == 0 && this.backButton != undefined)
                this.backButton.visible = true;
        });

        this._events.add(this.childOpened, (sender) => {
            if (!this._childForms.includes(sender)) { // Některé typy formulářů by se mohly bez kontroly přidat vícekrát. Např. ChartFrameFormBuilder.build produkuje 2 eventy pro stejný form.
                this._childForms.push(sender);
            }
            if (this.backButton != undefined)
                this.backButton.visible = false;
        });
    }

    private _setFormLayout(): void {
        if (this._formLayout !== FormLayout.none) {
            if (this._formLayout === FormLayout.horizontal)
                this.setHorizontalLayout();
            else
                this.setVerticalLayout();

            return;
        }

        const formlayout = this.getDataAttr("FormLayout");
        // Jestliže ve formLayout je nastaveno horizontal, a nebo na formuláři je pouze SplitPanel a ten má v sobě TreeViewFrame, tak se spustí horizontální zobrazení, jinak vertikální.
        if ((formlayout && formlayout.toLowerCase() === "horizontal") ||
            (this._formInternalPanel && this._formInternalPanel.children.length === 1 && (this._formInternalPanel.children[0].tagName.toLowerCase() === SplitPanel.is) &&
                Array.from(this._formInternalPanel.children[0].children).some(child => (child as IHTMLElementWithControl).tscControl instanceof ITreeViewFrame))
        )
            this.setHorizontalLayout();
        else
            this.setVerticalLayout();
    }

    private _initFilterPanel(): void {
        this._outerPanel.classList.add("OuterPanel");
        if (this._formInternalPanel != null)
            this._outerPanel.appendChild(this._formInternalPanel);
        this.filterPanel.ownerForm = this;
        this._outerPanel.appendChild(this.filterPanel);
        this._outerPanel.appendChild(this.formSettingsPanel);

        this.element.appendChild(this._outerPanel);
    }

    //#region OldRights

    /**
     * Z definice formuláře vytvoří práva na formuláři
     */
    private async _initializeFormRights(): Promise<void> {
        // Načtení podmínek použitých na formuláři
        const conditions = helper.toArray<IHTMLElementWithControl>(this.element.querySelectorAll("span[data-tscitem='ConditionDef']"));
        for (const aktCond of conditions) {
            const condValue = aktCond.getAttribute("value");
            const condId = aktCond.getAttribute("condid");
            this.conditionItems.push({ id: condId, value: condValue });
            ConditionStore.addCondition(condId, condValue, DataConverter.toBoolean(aktCond.getAttribute("localevaluable")));
        }

        // Načtení formulářových práv
        const rightDefinitionElements = helper.toArray<HTMLUnknownElement>(this.element.querySelectorAll("[data-tscitem='FormRight']"));

        const roleIDs = this.formContext?.roles?.length > 0 ? this.formContext.roles : await OWManager.getUserRoleIDs();

        //Načtení defaultních práv prvků
        const implicitAllRolesFormRights = await OWManager.getAllRolesDefaultRights(this.formName, RightLevel.allRolesFormLevel, [ROLELESS_ROLE_ID], EMPTY_REQUEST_CONTEXT);
        const implicitRolesFormRights = await OWManager.getAllRolesDefaultRights(this.formName, RightLevel.formLevel, roleIDs, EMPTY_REQUEST_CONTEXT);

        for (const rightDefinitionElement of rightDefinitionElements) {
            const level = DataConverter.toNumber(parseRequiredRightElementAttribute<"level">(rightDefinitionElement, "level"));
            const newRight = FormRightsExtractor.extractRightsFromElement(rightDefinitionElement, level === RightLevel.allRolesFormLevel ? implicitAllRolesFormRights : implicitRolesFormRights);

            // Zajímají nás práva pouze pro aktivní role a defaultní roli
            if (newRight.roleID !== ROLELESS_ROLE_ID && roleIDs.indexOf(newRight.roleID) === -1) {
                continue;
            }

            // Podmínky pro editaci a mazání se používají a vyhodnocují při každé změně záznamu
            if (newRight.deleteConditionID && this.usedConditionItemsId.indexOf(newRight.deleteConditionID + "") === -1)
                this.usedConditionItemsId.push(newRight.deleteConditionID + "");
            if (newRight.editConditionID && this.usedConditionItemsId.indexOf(newRight.editConditionID + "") === -1)
                this.usedConditionItemsId.push(newRight.editConditionID + "");

            this.formRights.push(newRight);
        }

        // Vyhodnocení práva zakládání,čtení a mazání se provádí pouze jednou při inicializaci formuláře
        this._canNew = await this._initializeFormRight(roleIDs, RightType.insert);
        this._canRead = await this._initializeFormRight(roleIDs, RightType.read);
        this._canDelete = await this._initializeFormRight(roleIDs, RightType.delete);
    }

    /**
     * Vyhodnocení formulářového práva
     * @param roleIDs Seznam aktivních rolí
     * @param rightType Typ práva
     */
    private async _initializeFormRight(roleIDs: readonly string[], rightType: RightType): Promise<boolean> {
        let right: boolean | null = null;

        // Nejdřív se vyhodnotí právo pro defaultní roli
        const formRightWithoutRole = this.formRights.find(r => r.roleID === ROLELESS_ROLE_ID);

        if (formRightWithoutRole) {
            right = await this._getRightByType(formRightWithoutRole, rightType);
            if (right === false) { // Jakmile DEFAULTNÍ role právo zakáže, už ho žádná role nemůže povolit
                return right;
            }
        }

        for (const formRight of this.formRights.filter(formRight => roleIDs.indexOf(formRight.roleID) !== -1)) {
            if (right) { // Jakmile jedna role právo povolí, tak už ho jiná nemůže zakázat
                break;
            }

            right = await this._getRightByType(formRight, rightType);
        }

        if (rightType == RightType.insert) {
            this._canInsertForm = right;
        }

        return right !== null ? right : true; // Pokud nebylo nalezeno žádné právo na úrovni formulářů, tak je defaultně povoleno
    }

    /**
     * VYhodnotí právo podle požadovaného typu
     * @param formRight Formulářové právo
     * @param rightType Typ práva
     */
    private async _getRightByType(formRight: IFormRight, rightType: RightType): Promise<boolean> {
        let right: boolean | null = null;

        switch (rightType) {
            case RightType.insert:
                if (formRight.insertConditionID) { // Pokud je právo podmínkou, tak ji je potřeba vyhodnotit
                    right = formRight.insert && (formRight.isInsertConditionNegative !== await OWManager.evaluateConditionWithoutObject(formRight.insertConditionID));
                } else {
                    right = formRight.insert;
                }
                break;

            case RightType.read:
                right = formRight.read;
                break;

            case RightType.delete:
                right = formRight.delete;
                break;
        }

        return right !== null ? right : true; // Pokud nebylo nalezeno žádné právo na úrovni formulářů, tak je defaultně povoleno
    }
    //#endregion OldRights

    /**
     * Odstranění komponent z definice formuláře, které jsou pro danou roli neviditelné.
     * Proto se pro ně nemusí vyhodnocovat práva po změně AO
     * @param element - definice formuláře
     */
    public static async removePernamentInvisibleElementsFromDefinitions(element: HTMLElement): Promise<HTMLElement> {
        const elements = element.querySelectorAll("[data-tsccontrol]");

        // Projdu všechny tsccontroly na fomuláři a zjistím jestli jsou permanentně neviditelná nastavením práv v definici formuláře
        for (let i = 0; i < elements.length; i++) {
            const aktElement = elements[i] as HTMLElement;
            if (!aktElement.id)
                aktElement.id = UIManager.instance.generateElementID();
            const visible = aktElement.parentElement.querySelector("#" + aktElement.id + " > " + "span[data-tscitem='Rights.Visible']");
            const aktRoleIDs = await OWManager.getUserRoleIDs();

            if (visible) {
                for (let j = 0; j < visible.childNodes.length; j++) {
                    const aktNode = visible.childNodes[j];
                    const roleIDofCondition = (aktNode as Element).attributes.getNamedItem("roleid").value;

                    // Element je permanentně visible==false na úrovni formuláře, proto ho odstraním z definice
                    if ((aktNode as Element).attributes.getNamedItem("value") && (aktNode as Element).attributes.getNamedItem("value").value == "false" && aktRoleIDs.indexOf(roleIDofCondition) > -1)
                        elements[i].parentNode.removeChild(elements[i]);
                }
            }
        }

        return Promise.resolve(element);
    }

    /** Zvětšení formuláře pro touch zařízení */
    private _enlargeForTouch(formDef: HTMLElement): void {
        const controls = formDef.querySelectorAll("[data-tsccontrol]");
        this._resizeCoeficient = Form.getCoef();

        for (let i = 0; i < controls.length; i++) {
            this._changeWidths(controls[i] as HTMLElement, this._resizeCoeficient);
        }
    }

    /** Získá přepočtový koeficient pro jednotlivé platformy. Koeficientem se násobí velikost a poloha controlu. */
    public static getCoef(): number {
        if (DeviceInfo.deviceProperties.touch)
            for (const i in Form.osCoef)
                if ((DeviceInfo.deviceProperties as any)[i])
                    return Form.osCoef[i];
        return 1;
    }

    /** Změna rozmeru controlu */
    private _changeWidths(el: HTMLElement, coef: number): void {
        const attrs = ["data-width", "data-height", "data-top", "data-left", "data-captionTop", "data-captionLeft"];

        attrs.forEach((attr) => {
            if (el.hasAttribute(attr) && isFinite(+el.getAttribute(attr)))
                el.setAttribute(attr, (+el.getAttribute(attr) * coef).toFixed(0));
        });
    }

    /**
     * Nastavení horizontálního(klasického) zobrazení formuláře
     */
    public setHorizontalLayout(): void {
        if (this._formInternalPanel == null)
            return;

        this.horizontalLayout = true;

        this._formInternalPanel.classList.add("horizontalLayout");
        this._formInternalPanel.classList.add("fits");

        if (this._formInternalPanel.children.length === 1 && (this._formInternalPanel.children[0].tagName.toLowerCase() === SplitPanel.is)) // Kvůli polyfillu nejde použít instanceof SplitPanel
            this._formInternalPanel.children[0].setAttribute("orientation", SplitterOrientation[SplitterOrientation.horizontal]); // Kvůli polyfillu nejde nastavovat pomocí property :( // sp.orientation = SplitterOrientation.horizontal;

        this._checkLists();
        Form._checkPages();
    }

    /**
     * Nastavení vertikálního zobrazení formuláře
     */
    public setVerticalLayout(): void {
        if (this._formInternalPanel == null)
            return;

        this.horizontalLayout = false;

        this._formInternalPanel.classList.remove("horizontalLayout");
        this._formInternalPanel.classList.add("fits");

        if (this._formInternalPanel.children.length === 1 && (this._formInternalPanel.children[0].tagName.toLowerCase() === SplitPanel.is)) { // Kvůli polyfillu nejde použít instanceof SplitPanel
            const spElement = this._formInternalPanel.children[0];
            if (Array.from(spElement.children).some(child => (child as IHTMLElementWithControl).tscControl instanceof ITreeViewFrame))
                spElement.setAttribute("orientation", SplitterOrientation[SplitterOrientation.horizontal]);
            else
                spElement.setAttribute("orientation", SplitterOrientation[SplitterOrientation.vertical]);
        }

        this._checkLists();

        const devProps = DeviceInfo.deviceProperties;
        if (devProps.touch && (devProps.iOS || devProps.android))
            Form._checkPages();
    }

    /**
     * Zkontroluje, zda je na formech v parametru nějaký neuložený záznam pokud ano zobrazí YNC dialog
     * @param forms seznam formulářů
     */
    public static async checkFormsIsEdited(forms: Form[]): Promise<boolean> {
        let editedFrames: IMasterableFrame[] = [];
        const frmObj: Array<{ frameList: IMasterableFrame[]; form: Form }> = [];
        for (let i = forms.length - 1; i > -1; i--) {
            const form = forms[i];
            const ef = form._checkMasterableFramesIfEdited();
            if (ef.length > 0) {
                editedFrames = editedFrames.concat(ef);
                frmObj.push({ frameList: ef, form: form });
                break;
            }
        }

        if (editedFrames.length === 0) {
            return true;
        } else {
            FormManager.instance.openForm(forms[0].formName, { formContext: forms[0].formContext });
            const res = await FrameSharedMethods.showSaveChangesDialog();
            switch (res.result) {
                case MessageButtonType.ok: {
                    try {
                        const prms: Array<Promise<boolean>> = [];
                        for (const form of frmObj) {
                            prms.push(form.form._saveFrames(form.frameList));
                        }

                        await Promise.all(prms);

                        for (const r of prms) {
                            if ((await r) == false)
                                return false;
                        }

                        return true;
                    } catch (err) {
                        ErrorHandler.instance.handleError(err);
                        return false;
                    }
                }
                case MessageButtonType.no: {
                    const prms: Array<Promise<void>> = [];
                    for (const form of frmObj) {
                        prms.push(form.form._cancelChangesOnFrames(form.frameList));
                    }
                    await Promise.all(prms);
                    return true;
                }
                case MessageButtonType.cancel:
                    return false;
            }
            return false;
        }
    }

    /**
     * Zkontroluje, zda je formulář rozeditovaný. Jestli jo, tak zobrazí YNC dialog, jestli se má uložit, zrušit změny nebo nedělat nic.
     * Když se otevíra nový formulář (žádný frame není rozeditovaný anebo v YNC dialogu bylo vybráno Ano/Ne), vrací true.
     * Když se zůstavá na současném formuláři (v dialogu zvoleno Storno), tak vrací false.
     * @param param - nastavení režimu ukládání framů
     */
    public override async checkAndSaveForm(param: IFormSavingParameters = { sourceFrame: null, cancelEditIfNotEdited: true, checkRecommended: false, checkPostponed: false, forceSave: false, formClosing: false, skipSavingEditContextId: false, cancelChanges: false, openedModalFrames: null }): Promise<boolean> {
        if (param.cancelEditIfNotEdited == undefined) param.cancelEditIfNotEdited = true;
        if (param.checkRecommended == undefined) param.checkRecommended = false;
        if (param.checkPostponed == undefined) param.checkPostponed = false;

        if (this.detailFrame && this.detailFrame instanceof IBussyAwaitable) {
            await this.detailFrame.waitForPendingUpdates();
            await this.detailFrame.waitForPendingActions();
        }

        const editingFrames = this._checkMasterableFramesIfEdited(param);
        const editedFrames: IMasterableFrame[] = [];

        if (param.cancelEditIfNotEdited)
            for (let i = 0, len = editingFrames.length; i < len; i++) {
                const currentFrame = editingFrames[i];
                /**
                 * Poznamka pro: currentFrame.isEditing && !currentFrame.isChanged
                 * Pokud se rozedituje záznam na framu (isEditing => true), ale nezmění se jeho data (isChanged = false), tak se zruší editace.
                 */
                if (currentFrame instanceof IEditableFrame && !(currentFrame instanceof IDetailFrame) && currentFrame.isEditing && (!currentFrame.isChanged && !currentFrame.isEditContextInitiator)) {
                    if (currentFrame instanceof IExtendedLifeCycle)
                        await currentFrame.unlockObject();
                    if (!param.formClosing)
                        currentFrame.isEditing = false;
                } else {
                    editedFrames.push(currentFrame);
                }
            }

        if (editedFrames.length === 0) {
            return true;
        } else {
            if (MessageWindow.countDisplayMessage > 0) {
                return false;
            }

            if (param.formClosing)
                param.forceSave = editedFrames.every(frm => frm.suppressSaveDialog == true);

            const res = param.forceSave ? { result: MessageButtonType.ok } : await (async () => {
                const element = GenericControl.findParent(editedFrames[0].element, (el) => el.classList.contains("Page")) as IHTMLElementWithControl;
                if (element && element.tscControl instanceof Page)
                    element.tscControl.tab.pageTabClick(); // Zaktivnění první rozeditované záložky
                return await FrameSharedMethods.showSaveChangesDialog();
            })();

            if (param.cancelChanges) {
                res.result = MessageButtonType.no;
            }

            switch (res.result) {
                case MessageButtonType.ok:
                    try {
                        const result = await this._saveFrames(editedFrames, param.checkRecommended, param.checkPostponed, param.forceSave, param.formClosing, param.skipSavingEditContextId);
                        this._setActiveMainForm();
                        return result;
                    } catch (err) {
                        ErrorHandler.instance.handleError(err);
                        return false;
                    }

                case MessageButtonType.no:
                    await this._cancelChangesOnFrames(editedFrames, param.sourceFrame);
                    this._setActiveMainForm();
                    return true;
                case MessageButtonType.cancel:
                    return false;
            }
            return false;
        }
    }

    private _setActiveMainForm(): void {
        if (this.ownerForm && this.isModal) {
            let form = this.ownerForm;
            while (form.isModal && form.ownerForm != undefined) {
                form = form.ownerForm;
            }
            if (!form.getState(WindowState.active)) form.setState(WindowState.active, true);
        } else {
            if (!this.getState(WindowState.active)) this.setState(WindowState.active, true);
        }
    }

    /**
     * Projde a uloží framy, které dostane v parametru
     * @param frames framy, které se budou ukládat
     * @param checkRecommended zda se mají kontrolovat doporučená pole
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @param skipValidation Pokud je true, tak klient nebude validovat pole
     * @param isFormClosing Příznak zdali se zavírá formulář
     * @param skipSavingEditContextId Příznak zdali se má přeskočit ukládání editačního kontextu
     * @return Vrací true pokud ukládání proběhlo v pořádku (všechny framy vrátily null) a false pokud některý frame vrátil výjimku nebo prázdné pole
     */
    private async _saveFrames(frames: IMasterableFrame[], checkRecommended = false, checkPostponed = false, skipValidation = false, isFormClosing?: boolean, skipSavingEditContextId?: boolean): Promise<boolean> {
        try {
            const frameResults: Array<Promise<SaveResultData[] | null>> = [];

            for (const frame of frames) {
                if (frame instanceof IEditableFrame || frame instanceof ICalendarFrame) {
                    if (frame instanceof IDetailMasterableFrame && frame.detail instanceof IEditableFrame) {
                        frameResults.push(frame.detail.saveRecords(null, checkRecommended, checkPostponed, skipValidation && (frame.useMasterContext || frame.isEditContextInitiator), isFormClosing, skipSavingEditContextId)); // Chyby z ukládání je nutné zobrazit i na detailu
                        continue;
                    }

                    frameResults.push(frame.saveRecords(null, checkRecommended, checkPostponed, skipValidation && (frame.useMasterContext || frame.isEditContextInitiator), isFormClosing, skipSavingEditContextId));
                } else if (frame instanceof ISaveable) {
                    frameResults.push(frame.save(skipSavingEditContextId));
                }
            }

            const results = await Promise.all(frameResults);

            return results.every((result) => result === null);
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return false;
        }
    }

    /**
     * Projde framy, které dostane v parametru a zruší na nich aktualní změny
     * @param frames framy, na kterých se budou rušit změny
     * @param sourceFrame frame, ze kterého bylo vývoláno zrušení změn
     */
    private async _cancelChangesOnFrames(frames: IMasterableFrame[], sourceFrame?: IMasterableFrame): Promise<void> {
        const prms: Array<Promise<any>> = [];
        for (const frame of frames) {
            if (frame instanceof IEditableFrame || frame instanceof ICalendarFrame) {
                if (sourceFrame && sourceFrame === frame) {
                    continue;
                }
                prms.push(frame.cancelChanges());
            }
        }

        await Promise.all(prms);
    }

    private _checkLists(): void {
        helper.toArray<HTMLElement>(this.element.getElementsByClassName("ListFrame")).forEach((list) => {
            const currentList = ((list as IHTMLElementWithControl).tscControl as ListFrame);
            if (currentList)
                currentList.checkIfIsCompact();
        });
    }

    // iPhone + Android mobil - záložky do selectu
    private static _checkPages(): void {
        const pages = helper.toArray<IHTMLElementWithControl>(document.querySelectorAll("[data-tsccontrol=\"Pages\"]")).map((el) => {
            return el.tscControl as Pages;
        });
        pages.forEach((el) => {
            el?.checkTabsEventMethod();
        });
    }

    public toggleLayout(): void {
        if (this.horizontalLayout)
            this.setVerticalLayout();
        else
            this.setHorizontalLayout();

        Form.layoutChangedEventMethod();
    }

    /**
     * Zavření modalního okna
     */
    public async closeModalWindow(): Promise<void> {
        await this.close();
    }

    public static layoutChangedEventMethod(): void {
        Form.formLayoutChanged.trigger(null, null);
    }

    /** Přizpůsobení formu rozměrům okna */
    public static checkFormSizeAfterShown(sender: Form): void {
        if (sender._formInternalPanel == null) {
            return;
        }

        sender._formInternalPanel.classList.add("fits");

        if (sender && sender.horizontalLayout) {
            sender.setHorizontalLayout();
        }
    }

    public async showProfilesPanel(): Promise<void> {
        await this._profileManager.createFromSettingsPanel();
        this.formSettingsPanel.show();
    }

    /**
     * Zruší aktivní profil a nastaví formulář do výchozího stavu
     */
    public override clearProfileInfo(): void {
        for (const sp of this.element.querySelectorAll(SplitPanel.is) as NodeListOf<SplitPanel>) {
            const defaultPos = this._splitPanelDefaultSplitterPositions.get(sp.id);
            if (defaultPos != null)
                sp.currentSplitterPosition = defaultPos;
        }

        this._queryMode.profileValue = null;
    }

    /**
    * Přidá side panel
    * @param panel hmtlElement přidávaného panelu
    */
    public override addSidePanel(panel: HTMLElement): void {
        this._outerPanel.appendChild(panel);
    }

    private _checkUniqueIds(): void {
        // z výkonnostních důvodů kontrola jen v dev módu
        if (!AppContext.instance.configuration.developmentMode)
            return;

        const elementsWithId = [...this.element.querySelectorAll("[id]")];
        const uniqueIds = new Set();
        const duplicateIds = new Set();

        for (const el of elementsWithId) {
            if (uniqueIds.has(el.id))
                duplicateIds.add(el.id);
            else
                uniqueIds.add(el.id);
        }

        if (duplicateIds.size === 0)
            return;

        this._logger.error("Form contains elements with multiple identical ids. Html id attribute must be unique and some functionality may not be working correctly! Check an 'I' attribute in form definition.", ...duplicateIds);
    }

    /**
     * Smaže `listener` z windowResized eventy a také z pole listenerů ve `FormSizeComparer`.
     * @param listener který bude smazán
     * @private
     */
    private _clearResizeListener(listener: IListener): void {
        if (listener) {
            UIManager.instance.windowResized.remove(listener);
            FormSizeComparer.listeners = ArrayUtils.remove(FormSizeComparer.listeners, listener);
        }
    }

    //#endregion Methods

    //#region IWindowable
    /**
     * Nastaví Form podle vstupních dat
     * @param paramData Vstupní data
     */
    public override setParametricData(paramData: IFRMParams): void {
        if (paramData.selectedID != null)
            this.selectedId = paramData.selectedID;

        if (this._parametricDataFirstSet) { // Při opakovném setu parametrů se přenastaví jen výše uvedené
            if (this.selectedId != null && this.mainFrame && this.mainFrame instanceof ISpecificallyLoadableFrame) // Při znovunastavení selectedId se pokusíme na mainFramu označit záznam s daným ID
                this.mainFrame.loadAndSelectRecord(this.selectedId, true);
            return;
        }

        this._isDetailForm = !!paramData.detailFormMasterFrame || !!paramData.detailFormObject;
        this._disableQueryMode = !!paramData.condition || !!paramData.masterLinkToAttr;

        if (paramData.detailFormMasterFrame != null)
            this.detailFormMasterFrame = paramData.detailFormMasterFrame;
        this.visibleOnlyDetail = paramData.visibleOnlyDetail ?? this._isDetailForm;

        if (paramData.menuItemId)
            this.menuItemId = paramData.menuItemId;
        if (paramData.masterLinkToAttr != null)
            this.masterLinkToAttr = paramData.masterLinkToAttr;
        if (paramData.condition != null)
            this.formCondition = paramData.condition;
        if (paramData.conditionParamsAttrs != null)
            this.formConditionParamAttrs = paramData.conditionParamsAttrs;
        if (paramData.conditionParamsObject != null)
            this.formConditionParamsObject = paramData.conditionParamsObject;
        if (paramData.hideWindowActions != null)
            this.hideWindowActions = paramData.hideWindowActions;
        if (paramData.inLOVMode != null)
            this.isLov = paramData.inLOVMode;
        if (paramData.enableLOVSelect != null)
            this.enableLovSelect = paramData.enableLOVSelect;
        if (paramData.isModal != null)
            this.isModal = paramData.isModal;
        if (paramData.viewMode != null) // Musí být vždy vyhodnoceno přes openMode
            this.viewMode = paramData.viewMode;
        if (paramData.applyLOVCondition != null)
            this.applyLovCondition = paramData.applyLOVCondition;
        if (paramData.applyFormProfileId != null)
            this._initialFormProfileId = paramData.applyFormProfileId;
        if (paramData.setValues != null)
            this.setValues = paramData.setValues;
        if (paramData.ownerForm != null)
            this.ownerForm = paramData.ownerForm;
        if (paramData.executerFrame != null)
            this.executerFrame = paramData.executerFrame;
        if (paramData.frameRefreshType != null)
            this.frameRefreshType = paramData.frameRefreshType;
        if (paramData.formLayout != null)
            this._formLayout = paramData.formLayout;
        if (paramData.openInEdit != null && this.viewMode != true) // viewMode má vyšší prioritu a může tak zrušit openInEdit
            this.openInEdit = paramData.openInEdit;
        if (paramData.detailFormObject != null)
            this._detailFormObject = paramData.detailFormObject;
        if (paramData.showDetail != null)
            this.showDetail = paramData.showDetail;
        if (paramData.openMode != null && this.viewMode != true) // viewMode má vyšší prioritu a může tak zrušit openMode (dle zadání 11864)
            this.openMode = paramData.openMode;
        if (paramData.hideButtons != null)
            this._hideButtons = paramData.hideButtons;
        if (paramData.purposeType != null)
            this.purposeType = paramData.purposeType;
        if (paramData.autoSelectFirstRow != null)
            this.autoSelectFirstRow = paramData.autoSelectFirstRow;
        if (paramData.autoSelectOnlyIfOneRow != null)
            this.autoSelectOnlyIfOneRow = paramData.autoSelectOnlyIfOneRow;
        if (paramData.frameToRefreshAfterSetFormActive != null)
            this.frameToRefreshAfterSetFormActive = paramData.frameToRefreshAfterSetFormActive;
        if (paramData.insertObjectName != null)
            this._insertObjectName = paramData.insertObjectName;
        if (paramData.readVersioned != null)
            this._readVersioned = paramData.readVersioned;
        if (paramData.setAttributesOnNewObject != null) {
            this.setAttributesOnNewObject = "";
            paramData.setAttributesOnNewObject.forEach(val => this.setAttributesOnNewObject += `${val.attrName}=${val.value};`);
        }
        if (paramData.isImplicit != null) {
            this._isImplicit = paramData.isImplicit;
        }

        if (paramData.masterObjectList != null)
            this.masterObjectList = paramData.masterObjectList;

        if (paramData.masterObject != null)
            this.masterObject = paramData.masterObject; // Vyvolá změnu masterObjectu, takže by bylo ideální to dát na konec

        this._parametricDataFirstSet = true;

        if (paramData.messages) // Pokud existustují zprávy, zobrazí se při otevření formuláře.
            this._resultMessages = paramData.messages;

        this._returnMainInstance = paramData.returnMainInstance;
        if (paramData.formContext != null)
            this.formContext = { ...paramData.formContext, rightsSettingsVersion: this._rightsSettingsVersion };
        else {
            this.formContext = {
                roles: AppContext.instance.activeUserRolesIds,
                rightsSettingsVersion: this._rightsSettingsVersion,
            };
        }
        if (paramData.transactionContextGetter) {
            this._transactionContextGetter = paramData.transactionContextGetter;
        }

        this._multiEditMode = paramData.multiEditMode;
    }

    //#endregion IWindowable

    //#region IDisposable
    public override dispose(): void {
        if (this.element) {
            if (this._splitterMoveListener)
                this.element.removeEventListener("splittermove", this._splitterMoveListener);

            this.element.removeEventListener("click", this._clickListener);
        }

        if (this._keyupControlListener) {
            window.removeEventListener("keyup", this._keyupControlListener);
        }

        this.stateChanged.removeAll();
        this.titleChanged.removeAll();
        this.closed.removeAll();

        FormManager.instance.formOpened.remove(this._formOpenedListener);
        FormManager.instance.formSwitched.remove(this._formSwitchedListener);

        this._widgetIntersectionObserver?.disconnect();

        for (const item of this._outerPanel.children) {
            if (item instanceof IDisposable)
                item.dispose();
        }

        super.dispose();
        (this._formInternalPanel as IHTMLElementWithControl)?.tscControl?.dispose(); // musí být až po volání předka, jinak se nedisposnou potomci
    }
    //#endregion IDisposable
}

export enum FormDesign {
    phone = 1, // Zobrazení jen jednoho framu
    tablet = 2, // Data v sloupcích
    desktop = 3, // Standartní zobrazení definované v XGenu
}
